import clsx from "clsx";
import { FC } from "react";
import { defineMessages, MessageDescriptor, useIntl } from "react-intl";
import { OrganizationRole } from "~/api/model";
import { DisplayFor } from "~/components/permissions/DisplayFor";
import { trackEvent } from "~/utils/analytics";
import { SettingsMenuItem } from "./types";

type NavMenuProps = {
  onItemClick: (item: SettingsMenuItem) => void;
  activeItem: SettingsMenuItem;
  availableMenuItems: SettingsMenuItem[];
};

const messages: Record<SettingsMenuItem, MessageDescriptor> = defineMessages({
  account: {
    id: "settings.account",
    defaultMessage: "Account",
  },
  appearance: {
    id: "settings.appearance",
    defaultMessage: "Appearance",
  },
  organization: {
    id: "settings.organization",
    defaultMessage: "Organization",
  },
  team: {
    id: "settings.team",
    defaultMessage: "Team",
  },
  categoriesManagement: {
    id: "settings.categoriesManagement",
    defaultMessage: "Categories",
  },
  knowledge: {
    id: "settings.knowledge",
    defaultMessage: "Knowledge",
  },
  reports: {
    id: "settings.reports",
    defaultMessage: "Reports",
  },
  planAndUsage: {
    id: "settings.planAndUsage",
    defaultMessage: "Billing",
  },
  analytics: {
    id: "settings.analytics",
    defaultMessage: "Analytics",
  },
  developers: {
    id: "settings.developers",
    defaultMessage: "Developers",
  },
  archived: {
    id: "settings.archived",
    defaultMessage: "Archive",
  },
  deletedDocuments: {
    id: "settings.deletedDocuments",
    defaultMessage: "Deleted Documents",
  },
});

const ADMIN_ONLY_ITEMS: ReadonlySet<SettingsMenuItem> = new Set([
  "organization",
  "team",
  "knowledge",
  "planAndUsage",
  "analytics",
]);

const GROUP_START_ITEMS: ReadonlySet<SettingsMenuItem> = new Set([
  "organization",
  "categoriesManagement",
  "planAndUsage",
  "developers",
]);

export const NavMenu: FC<NavMenuProps> = ({
  onItemClick,
  activeItem,
  availableMenuItems,
}) => {
  const { formatMessage } = useIntl();

  const handleItemClick = (item: SettingsMenuItem) => {
    trackEvent({
      eventName: `app/settings_account/menu.links/click`,
      properties: {
        item,
      },
    });
    onItemClick(item);
  };

  const renderMenuItem = (item: SettingsMenuItem) => (
    <li
      key={item}
      className={clsx(
        "py-2 px-3 flex-col w-full sm:w-[266px] rounded-md cursor-pointer",
        GROUP_START_ITEMS.has(item) && "sm:mt-4",
        activeItem === item
          ? "bg-accent text-accent-foreground"
          : "text-foreground bg-background hover:bg-accent hover:text-accent-foreground"
      )}
      onClick={() => handleItemClick(item)}
    >
      <span className="text-sm font-medium">
        {formatMessage(messages[item])}
      </span>
    </li>
  );

  return (
    <ul className="flex flex-col w-full sm:w-[266px] sm:mr-6">
      {availableMenuItems.map((item) =>
        ADMIN_ONLY_ITEMS.has(item) ? (
          <DisplayFor
            key={item}
            roles={[OrganizationRole.SUPERADMIN, OrganizationRole.OWNER]}
          >
            {renderMenuItem(item)}
          </DisplayFor>
        ) : (
          renderMenuItem(item)
        )
      )}
    </ul>
  );
};
